import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from '../contexts/AuthContext';
import { rateMyTradeAPI } from '../utils/api';
import { mergeLeagueRollovers } from './leagueUtils';

/**
 * ShellContext — cross-feature state for the dashboard shell.
 * See .claude/SHELL-REDESIGN-PLAN.md.
 *
 * Holds: resolved Sleeper persona, merged leagues (rollover chains collapsed),
 * active league (persisted), and the draft registry that powers nav badges and
 * Home resume cards. Heavy feature data (sim results, intel trades) stays in
 * the features — only the lightweight index lives here.
 */

const ShellContext = createContext(null);

export const useShell = () => {
  const context = useContext(ShellContext);
  if (!context) {
    throw new Error('useShell must be used within ShellProvider');
  }
  return context;
};

const DRAFTS_KEY = 'ft_shell_drafts';

const readDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFTS_KEY)) || {};
  } catch {
    return {};
  }
};

export const ShellProvider = ({ children }) => {
  const { user } = useAuth();
  const sleeperUsername = user?.sleeperUsername || null;

  const [sleeperUser, setSleeperUser] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  // Ephemeral view context — set by league-scoped views (e.g. the simulator)
  // so the topbar chip reflects the league on screen, even one the user
  // doesn't belong to. Cleared when the view unmounts; never persisted.
  // There is deliberately NO global "active league": flows pick their own
  // league, so the chip only exists while a league is actually on screen.
  const [contextLeague, setContextLeague] = useState(null);
  const [drafts, setDrafts] = useState(readDrafts);

  useEffect(() => {
    if (!sleeperUsername) {
      setSleeperUser(null);
      setLeagues([]);
      return undefined;
    }
    let active = true;
    setLeaguesLoading(true);
    rateMyTradeAPI
      .getUserLeagues(sleeperUsername)
      .then((res) => {
        if (!active) return;
        setSleeperUser(res.data.user || null);
        setLeagues(mergeLeagueRollovers(res.data.leagues || []));
      })
      .catch(() => {
        if (active) setLeagues([]);
      })
      .finally(() => {
        if (active) setLeaguesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sleeperUsername]);

  // League-scoped views may register a switch handler so picking a league from
  // the topbar dropdown navigates within the feature (e.g. straight to that
  // league's simulator). Held in state so the topbar knows whether the chip
  // should offer a dropdown at all.
  const [leagueSwitchHandler, setLeagueSwitchHandlerState] = useState(null);
  const setLeagueSwitchHandler = useCallback((fn) => {
    setLeagueSwitchHandlerState(() => fn);
  }, []);
  const requestLeagueSwitch = useCallback(
    (league) => {
      if (leagueSwitchHandler) leagueSwitchHandler(league);
    },
    [leagueSwitchHandler]
  );

  /**
   * Draft registry — lightweight index of in-progress flows.
   * key: `${featureKey}:${entityKey}` (e.g. "trade-builder:league123:roster7")
   * value: { featureKey, label, resumeUrl, updatedAt } — payloads live with the feature.
   */
  const registerDraft = useCallback((key, draft) => {
    setDrafts((prev) => {
      const next = { ...prev, [key]: { ...draft, updatedAt: Date.now() } };
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearDraft = useCallback((key) => {
    setDrafts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const draftsForFeature = useCallback(
    (featureKey) =>
      Object.entries(drafts)
        .filter(([, d]) => d.featureKey === featureKey)
        .map(([key, d]) => ({ key, ...d })),
    [drafts]
  );

  const value = useMemo(
    () => ({
      sleeperUser,
      leagues,
      leaguesLoading,
      contextLeague,
      setContextLeague,
      canSwitchLeague: !!leagueSwitchHandler,
      setLeagueSwitchHandler,
      requestLeagueSwitch,
      drafts,
      registerDraft,
      clearDraft,
      draftsForFeature,
    }),
    [
      sleeperUser,
      leagues,
      leaguesLoading,
      contextLeague,
      leagueSwitchHandler,
      setLeagueSwitchHandler,
      requestLeagueSwitch,
      drafts,
      registerDraft,
      clearDraft,
      draftsForFeature,
    ]
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
};

export default ShellContext;
